import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import type { ChatAddToolApproveResponseFunction } from 'ai';

export type PendingToolApproval = {
  approvalId: string;
  input: unknown;
  title: string;
  toolCallId: string;
  toolName: string;
  /** What approving for good covers ("Always allow Notes"), when the request offers it. */
  remember?: string;
  /** What the operation acts on, as an image URL (an app's icon). */
  icon?: string;
};

/** The user's answer to an approval; `remember` approves this kind of operation for good. */
export type ToolApprovalAnswer = Parameters<ChatAddToolApproveResponseFunction>[0] & {
  remember?: boolean;
};
export type AnswerToolApproval = (answer: ToolApprovalAnswer) => Promise<void>;

/** What the core added to an approval's tool call (`toolMetadata.crowdex`). */
function approvalMetadata(metadata: unknown): { remember?: string; icon?: string } {
  if (typeof metadata !== 'object' || metadata === null) return {};
  const crowdex = (metadata as { crowdex?: unknown }).crowdex;
  if (typeof crowdex !== 'object' || crowdex === null) return {};
  const { remember, icon } = crowdex as { remember?: unknown; icon?: unknown };
  return {
    ...(typeof remember === 'string' ? { remember } : {}),
    // Only an image the core made inline, never a URL a tool could point anywhere.
    ...(typeof icon === 'string' && icon.startsWith('data:image/png;base64,') ? { icon } : {}),
  };
}

/** Pending AI SDK approvals, in transcript order, without any out-of-band ACP state. */
export function pendingToolApprovals(messages: readonly CrowdexUIMessage[]): PendingToolApproval[] {
  const message = messages.at(-1);
  if (message?.role !== 'assistant') return [];
  const lastStepStartIndex = message.parts.reduce(
    (lastIndex, part, index) => (part.type === 'step-start' ? index : lastIndex),
    -1,
  );
  const approvals: PendingToolApproval[] = [];
  const seen = new Set<string>();
  for (const part of message.parts.slice(lastStepStartIndex + 1)) {
    if (part.type !== 'dynamic-tool' || part.state !== 'approval-requested') continue;
    if (seen.has(part.approval.id)) continue;
    seen.add(part.approval.id);
    approvals.push({
      ...approvalMetadata(part.toolMetadata),
      approvalId: part.approval.id,
      input: part.input,
      title: part.title ?? part.toolName,
      toolCallId: part.toolCallId,
      toolName: part.toolName,
    });
  }
  return approvals;
}

export function hasPendingToolApproval(messages: readonly CrowdexUIMessage[]): boolean {
  return pendingToolApprovals(messages).length > 0;
}
